import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { ActionPlanReflection, ApiResponse } from '@/types'

// Fetch reflections for an action plan
export function useReflections(planId: string | undefined) {
  return useQuery({
    queryKey: ['reflections', planId],
    queryFn: async (): Promise<ActionPlanReflection[]> => {
      const res = await fetch(`/api/action-plans/${planId}/reflections`)
      const data: ApiResponse<ActionPlanReflection[]> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch reflections')
      return data.data!
    },
    enabled: !!planId,
  })
}

// Create or update reflection (upsert)
export function useCreateReflection() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ planId, ...body }: {
      planId: string
      what_worked?: string
      what_didnt_work?: string
      general_thoughts?: string
      effectiveness_rating: number
    }) => {
      const res = await fetch(`/api/action-plans/${planId}/reflections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const data: ApiResponse<{ id: string }> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to save reflection')
      return data.data!
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['reflections', variables.planId] })
    },
  })
}

// Delete reflection
export function useDeleteReflection() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id }: { id: string; planId: string }) => {
      const res = await fetch(`/api/action-plan-reflections/${id}`, { method: 'DELETE' })
      const data: ApiResponse<void> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to delete reflection')
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['reflections', variables.planId] })
    },
  })
}
